import * as RadixDialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { IconClose } from "./icons.tsx";

/**
 * Modal sobre Radix Dialog.
 *
 * Radix é usado aqui exatamente pelo motivo que o briefing autoriza: ele
 * resolve foco preso, restauração de foco ao fechar, Escape, `aria-modal` e
 * inertização do fundo. A aparência é integralmente nossa — nenhum estilo do
 * Radix é herdado.
 */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** `right` = painel Add Event do Ofative (360px). */
  placement?: "center" | "right";
}

const SIZES = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
} as const;

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  placement = "center",
}: DialogProps) {
  const isRight = placement === "right";

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            "dream-overlay fixed inset-0 z-50 motion-reduce:animate-none",
            isRight ? "bg-black/20" : "bg-[rgb(15_23_42_/_0.45)] backdrop-blur-[2px]",
          )}
        />
        <RadixDialog.Content
          className={cn(
            "dream-dialog-panel fixed z-50 flex flex-col overflow-hidden bg-[var(--color-surface)] focus:outline-none motion-reduce:animate-none",
            isRight
              ? "dream-dialog-panel--right inset-y-0 right-0 h-dvh w-full border-l border-[var(--color-line)] sm:w-[min(100vw,360px)]"
              : [
                  "dream-dialog-panel--center inset-0 h-dvh w-full max-h-dvh rounded-none",
                  "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100vw-2rem)] sm:max-h-[calc(100dvh-3rem)]",
                  "sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[var(--radius-xl)] sm:border sm:border-[var(--color-line)] sm:shadow-xl",
                  SIZES[size],
                ],
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-[var(--color-line)] px-4 py-3 sm:px-5 sm:py-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-[20px] font-bold tracking-[0] text-[var(--color-ink)]">
                {title}
              </RadixDialog.Title>
              {description !== undefined && (
                <RadixDialog.Description className="mt-0.5 text-[0.8125rem] text-[var(--color-ink-muted)]">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              aria-label="Fechar"
              className={cn(
                "-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
                "text-[var(--color-ink-muted)] transition-colors duration-[var(--dream-duration-fast)]",
                "hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
              )}
            >
              <IconClose className="size-4" />
            </RadixDialog.Close>
          </div>

          <div className="dream-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
            {children}
          </div>

          {footer !== undefined && (
            <div className="flex flex-col-reverse gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:px-5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  destructive = false,
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <button
            type="button"
            disabled={loading}
            onClick={() => onOpenChange(false)}
            className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3.5 text-[0.875rem] font-medium hover:bg-[var(--color-surface-subtle)] disabled:opacity-60 sm:h-9 sm:w-auto"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={cn(
              "h-10 w-full rounded-[var(--radius-md)] px-3.5 text-[0.875rem] font-medium text-white disabled:opacity-60 sm:h-9 sm:w-auto",
              destructive ? "bg-red-600 hover:bg-red-700" : "bg-brand-600 hover:bg-brand-700",
            )}
          >
            {loading ? "Aguarde..." : confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-[0.875rem] leading-5 text-[var(--color-ink-secondary)]">{message}</p>
    </Dialog>
  );
}
